import type { WsEvent } from './types';

type Listener = (event: WsEvent) => void;

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 15000;

/** Auto-reconnecting WS client with backoff and channel re-subscription on reconnect. */
export class WsClient {
  private socket: WebSocket | null = null;
  private readonly listeners = new Set<Listener>();
  private subscribedChannels: string[] = [];
  private attempt = 0;
  private closedByUser = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  connect(): void {
    this.closedByUser = false;
    this.open();
  }

  private open(): void {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const socket = new WebSocket(`${proto}://${window.location.host}/ws`);
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.attempt = 0;
      if (this.subscribedChannels.length > 0) this.subscribe(this.subscribedChannels);
    });

    socket.addEventListener('message', (ev) => {
      let payload: WsEvent | null = null;
      try {
        payload = JSON.parse(ev.data as string) as WsEvent;
      } catch {
        return;
      }
      for (const l of this.listeners) l(payload);
    });

    socket.addEventListener('close', () => {
      if (this.closedByUser) return;
      this.scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      socket.close();
    });
  }

  private scheduleReconnect(): void {
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** this.attempt, RECONNECT_MAX_MS);
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => this.open(), delay);
  }

  subscribe(channels: string[]): void {
    this.subscribedChannels = channels;
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ t: 'subscribe', channels }));
    }
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  close(): void {
    this.closedByUser = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.socket?.close();
    this.socket = null;
  }
}

export const wsClient = new WsClient();
